import { Component, computed, effect, inject, input, model, OnDestroy, OnInit, signal } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { AldermanshipDto } from '@shared/interfaces/dtos/aldermanships';
import {
  ClassificationCodeDto,
  ClassificationDetailDto,
  ClassificationDetailFolderDto,
  ClassificationFolderDto,
  ClassificationSheetDto,
} from '@shared/interfaces/dtos/classifications';
import { ServiceDto } from '@shared/interfaces/dtos/services';
import { MyRightsForPoints } from '@shared/models/rights';
import { ClassificationsService } from '@shared/services/classifications.service';
import { MoreDataSearchFormGroup } from '@shared/types/points';
import { TreeNode } from 'primeng/api';
import { TreeNodeSelectEvent } from 'primeng/tree';
import { finalize } from 'rxjs';
import { FormOptions } from 'src/app/common/models/form-options';
import { PointsAdvancedSearchService } from '../services/points-advanced-search.service';
import { PointsQuery } from '@shared/interfaces/points';
import { extractElementsFromQueryParam } from '@shared/helpers';
import { ClassificationFoldersQuery } from '@shared/interfaces/classifications';
import { MultiSelectChangeEvent } from 'primeng/multiselect';

@Component({
  selector: 'idel-more-data',
  templateUrl: './more-data.component.html',
  styleUrl: './more-data.component.scss',
})
export class MoreDataComponent implements OnInit, OnDestroy {
  private readonly pointsAdvancedSearchService = inject(PointsAdvancedSearchService);
  private readonly classificationsService = inject(ClassificationsService);
  public readonly formOptions = inject(FormOptions);

  public readonly form = model.required<FormGroup<MoreDataSearchFormGroup>>();

  public readonly services = input.required<TreeNode<ServiceDto>[]>();
  public readonly rights = input.required<MyRightsForPoints>();

  private readonly shouldPopulate = this.pointsAdvancedSearchService.shouldPopulateForms;
  private readonly shouldPopulateEff = effect(
    () => {
      const shouldPopulate = this.shouldPopulate();

      if (shouldPopulate) {
        this.populateFromCurrentSearch();
      }
    },
    { allowSignalWrites: true }
  );

  public readonly servicesIds = computed<number[]>(() => {
    const services = this.services();

    if (!services || !services.length) {
      return [];
    }

    return services.map((node) => node.data.id);
  });

  private readonly servicesIdsEff = effect(
    () => {
      const services = this.services();

      this.emptyClassifications();

      if (!services || !services.length || services.length > 1) {
        this.initOptions();
        return;
      }

      this.fetchClassificationOptionsBasedOnService();
    },
    { allowSignalWrites: true }
  );

  public readonly fetchingClassificationCodes = signal<boolean>(false);
  public readonly fetchingClassificationFolders = signal<boolean>(false);
  public readonly fetchingClassificationSheets = signal<boolean>(false);
  public readonly aldermanshipsOptions = signal<AldermanshipDto[]>([]);
  public readonly classificationCodes = signal<ClassificationCodeDto[]>([]);
  public readonly classificationCodesOptions = computed<(ClassificationCodeDto & { label: string })[]>(() =>
    this.classificationCodes().map((code) => ({
      ...code,
      label: `${code.code}${code.description ? ` - ${code.description}` : ''}`,
    }))
  );
  public readonly classificationFoldersOptions = signal<ClassificationFolderDto[]>([]);
  public readonly classificationSheetsOptions = signal<ClassificationSheetDto[]>([]);
  public readonly classificationsForAServiceNodes = signal<TreeNode<ClassificationDetailDto>[]>([]);
  public readonly showClassificationSelect = signal<boolean>(false);

  ngOnInit(): void {
    this.initOptions();
  }

  ngOnDestroy(): void {
    this.servicesIdsEff.destroy();
    this.shouldPopulateEff.destroy();
  }

  private populateFromCurrentSearch(): void {
    const currentSearch = this.pointsAdvancedSearchService.currentSearch();

    if (!currentSearch) return;

    this.pointsAdvancedSearchService.setFormPopulationProgressAsStartedFor('moreData');

    const selectedDecisionTypes = this.formOptions.decisionTypes().filter((decisionType) => {
      const decisionTypeIds = extractElementsFromQueryParam(currentSearch.TypeDecisionIds);

      if (!decisionTypeIds.length) return false;

      return decisionTypeIds.includes(decisionType.id.toString());
    });

    const selectedAldermanships = this.aldermanshipsOptions().filter((aldermanship) => {
      const aldermanshipIds = extractElementsFromQueryParam(currentSearch.EchevinatIds);

      if (!aldermanshipIds.length) return false;

      return aldermanshipIds.includes(aldermanship.id.toString());
    });

    let selectedClassificationCodes: ClassificationCodeDto[] = [];
    let selectedFolders: ClassificationFolderDto[] = [];
    let selectedSheets: ClassificationSheetDto[] = [];

    if ((currentSearch as PointsQuery & { classification?: [number, number, number] }).classification) {
      const [classificationCodeId, folderId, sheetId] = (
        currentSearch as PointsQuery & { classification?: [number, number, number] }
      ).classification!;

      const foundClassificationCode = this.classificationCodesOptions().find(
        (code) => code.id === classificationCodeId
      );
      const foundClassificationFolder = this.classificationFoldersOptions().find((folder) => folder.id === folderId);
      const foundClassificationSheet = this.classificationSheetsOptions().find((sheet) => sheet.id === sheetId);

      if (foundClassificationCode) {
        selectedClassificationCodes = [foundClassificationCode];
      }

      if (foundClassificationFolder) {
        selectedFolders = [foundClassificationFolder];
      }

      if (foundClassificationSheet) {
        selectedSheets = [foundClassificationSheet];
      }
    }

    this.form().patchValue({
      decisionTypes: selectedDecisionTypes ?? [],
      aldermens: selectedAldermanships ?? [],
      classifications: selectedClassificationCodes ?? [],
      folders: selectedFolders ?? [],
      sheets: selectedSheets ?? [],
    });

    this.pointsAdvancedSearchService.setFormPopulationProgressAsDoneFor('moreData');
  }

  private initOptions(): void {
    this.fetchClassificationCodes();
    this.fetchClassificationFolders();
    this.fetchClassificationSheets();
  }

  private emptyClassifications(): void {
    this.form().patchValue({
      classifications: [],
      folders: [],
      sheets: [],
    });
  }

  private fetchClassificationOptionsBasedOnService(): void {
    this.fetchClassificationCodes();
    this.fetchClassificationFolders();
  }

  private fetchClassificationFolders(classificationCodeId?: number): void {
    this.fetchingClassificationFolders.set(true);

    let query: ClassificationFoldersQuery | undefined = undefined;

    if (this.servicesIds().length === 1) {
      query = {
        ServiceId: this.servicesIds()[0],
      };
    }

    if (classificationCodeId) {
      query = {
        ...query,
        ClassementId: classificationCodeId,
      };
    }

    this.classificationsService
      .fetchClassificationFolders(query)
      .pipe(finalize(() => this.fetchingClassificationFolders.set(false)))
      .subscribe({
        next: (response: ClassificationFolderDto[]) => {
          this.classificationFoldersOptions.set(response);
        },
      });
  }

  private fetchClassificationSheets(folderId?: number): void {
    this.fetchingClassificationSheets.set(true);

    this.classificationsService
      .fetchClassificationSheets(folderId)
      .pipe(finalize(() => this.fetchingClassificationSheets.set(false)))
      .subscribe({
        next: (sheets) => this.classificationSheetsOptions.set(sheets),
      });
  }

  private fetchClassificationCodes(): void {
    this.fetchingClassificationCodes.set(true);

    this.classificationsService
      .fetchClassificationCodes(this.servicesIds())
      .pipe(
        finalize(() => {
          this.fetchingClassificationCodes.set(false);
        })
      )
      .subscribe({
        next: (response: ClassificationCodeDto[]) => {
          this.classificationCodes.set(response);
        },
      });
  }

  public searchForClassificationsBaseOnService(): void {
    const services = this.services();

    if (services.length !== 1) return;

    this.showClassificationAdvancedSearchSelect();
  }

  private showClassificationAdvancedSearchSelect(): void {
    const serviceNode = this.services()[0];

    if (!serviceNode) return;

    this.classificationsService.fetchClassificationForAService(serviceNode.data.id).subscribe({
      next: (response) => {
        this.classificationsForAServiceNodes.set(
          response.map((classification) => this.mapClassificationToTreeNode(classification))
        );
        this.showClassificationSelect.set(true);
      },
    });
  }

  private mapClassificationToTreeNode(classification: ClassificationDetailDto): TreeNode {
    return {
      label: classification.description,
      data: classification,
      icon: 'bi bi-archive-fill',
      styleClass: 'cursor-pointer',
      key: 'classementCode',
      children: classification.classeurs.map((folder) => ({
        label: folder.description,
        data: folder,
        icon: 'bi bi-folder-fill',
        key: 'folder',
        children: folder.chemises.map((sheet) => ({
          label: sheet.description,
          data: sheet,
          icon: 'bi bi-file-earmark-text',
          key: 'sheet',
        })),
      })),
    };
  }

  public onServiceClassificationSelect(event: TreeNodeSelectEvent): void {
    const { node: selectedNode } = event;

    if (!selectedNode) return;

    const { key } = selectedNode;

    if (key === 'classementCode') {
      const selectedNode = this.classificationCodesOptions().find(
        (classification) => classification.id === (event.node.data as ClassificationDetailDto).id
      );

      if (selectedNode) {
        this.form().controls.classifications.setValue([selectedNode]);
      }
    } else if (key === 'folder') {
      const selectedParentNode = this.classificationCodesOptions().find(
        (classification) => classification.id === (event.node.parent.data as ClassificationDetailDto).id
      );
      const selectedNode = this.classificationFoldersOptions().find(
        (folder) => folder.id === (event.node.data as ClassificationDetailFolderDto).id
      );

      if (selectedParentNode) {
        this.form().controls.classifications.setValue([selectedParentNode]);
      }

      if (selectedNode) {
        this.form().controls.folders.setValue([selectedNode]);
      }
    } else if (key === 'sheet') {
      const selectedGrandParentNode = this.classificationCodesOptions().find(
        (classification) => classification.id === (event.node.parent.parent.data as ClassificationDetailDto).id
      );
      const selectedParentNode = this.classificationFoldersOptions().find(
        (folder) => folder.id === (event.node.parent.data as ClassificationDetailFolderDto).id
      );
      const selectedNode = this.classificationSheetsOptions().find(
        (sheet) => sheet.id === (event.node.data as ClassificationSheetDto).id
      );

      if (selectedGrandParentNode) {
        this.form().controls.classifications.setValue([selectedGrandParentNode]);
      }

      if (selectedParentNode) {
        this.form().controls.folders.setValue([selectedParentNode]);
      }

      if (selectedNode) {
        this.form().controls.sheets.setValue([selectedNode]);
      }
    }

    this.showClassificationSelect.set(false);
  }

  public onClassificationCodeChange(event: MultiSelectChangeEvent): void {
    const selectedClassificationCodes = event.value as ClassificationCodeDto[];

    this.fetchClassificationFolders(
      selectedClassificationCodes.length === 1 ? selectedClassificationCodes[0].id : undefined
    );
  }

  public onClassificationFolderChange(event: MultiSelectChangeEvent): void {
    const selectedFolders = event.value as ClassificationFolderDto[];

    this.fetchClassificationSheets(selectedFolders.length === 1 ? selectedFolders[0].id : undefined);
  }
}
